import { Pencil, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ErrorState } from '@nucleus-commerce/admin-kit/errors';
import { FormSection } from '@nucleus-commerce/admin-kit/forms';
import { usePermissions } from '@nucleus-commerce/admin-kit/permissions';
import type { ChatTrigger } from '@nucleus-commerce/api-client/admin';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Badge } from '@nucleus-commerce/ui/components/badge';
import { Button } from '@nucleus-commerce/ui/components/button';
import { Skeleton } from '@nucleus-commerce/ui/components/skeleton';
import { Switch } from '@nucleus-commerce/ui/components/switch';

import { useChatAgents } from '@/features/conversations';
import { useStore } from '@/shared/store/store-context';

import { useChatSettings } from '../hooks/use-chat-settings';
import { useTriggerMutations } from '../hooks/use-trigger-mutations';
import { useTriggers } from '../hooks/use-triggers';

import { ConfirmDialog } from './confirm-dialog';
import { TriggerDialog } from './trigger-dialog';

const MAX_TRIGGERS = 30;

interface EditorState {
  trigger: ChatTrigger | null;
}

/**
 * Proactive messages (LC6): greetings the widget shows when its conditions hold, and rescuers the
 * API sends to a shopper still waiting for a first reply.
 */
export function TriggersTab({ storeId }: { storeId: string }) {
  const { t } = useTranslation('chatSettings');
  const format = useFormat();
  const store = useStore();
  const { can } = usePermissions();
  const canManage = can('conversations.manage');
  const query = useTriggers(storeId);
  const settings = useChatSettings(storeId);
  const agents = useChatAgents(storeId);
  const { toggle, remove } = useTriggerMutations(storeId);
  const [editor, setEditor] = useState<EditorState | null>(null);
  const [deleting, setDeleting] = useState<ChatTrigger | null>(null);

  if (query.isError) return <ErrorState error={query.error} onRetry={() => void query.refetch()} />;
  if (!query.data) return <Skeleton className="h-40 max-w-3xl" aria-busy="true" />;

  const triggers = [...query.data].sort((a, b) => a.position - b.position);
  const nextPosition = triggers.length === 0 ? 0 : Math.max(...triggers.map((x) => x.position)) + 1;

  return (
    <div className="flex max-w-3xl flex-col gap-6">
      <FormSection title={t('triggers.title')} description={t('triggers.description')}>
        {triggers.length === 0 ? (
          <p className="text-muted-foreground text-sm">{t('triggers.empty')}</p>
        ) : (
          <ul className="divide-y rounded-md border" aria-label={t('triggers.title')}>
            {triggers.map((trigger) => (
              <li key={trigger.id} className="flex items-start gap-3 p-3">
                <Switch
                  checked={trigger.enabled}
                  disabled={!canManage || toggle.isPending}
                  aria-label={t('triggers.toggle', { name: trigger.name })}
                  onCheckedChange={(enabled) => {
                    toggle.mutate({ id: trigger.id, enabled });
                  }}
                  className="mt-0.5"
                />
                <div className="min-w-0 flex-1 space-y-1">
                  <p className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-medium">{trigger.name}</span>
                    <Badge variant="secondary">{t(`triggers.kinds.${trigger.kind}`)}</Badge>
                    {trigger.oncePerVisitor ? (
                      <Badge variant="outline">{t('triggers.oncePerVisitorBadge')}</Badge>
                    ) : null}
                  </p>
                  <p className="text-muted-foreground text-xs">
                    {t('triggers.summary', {
                      conditions: trigger.conditions.length,
                      actions: trigger.actions.length,
                    })}
                    {' · '}
                    {t('triggers.fired', { count: trigger.firedCount })}
                    {trigger.lastFiredAt
                      ? ` · ${t('triggers.lastFired', { when: format.dateTime(trigger.lastFiredAt) })}`
                      : ''}
                  </p>
                </div>
                {canManage ? (
                  <div className="flex shrink-0 gap-1">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      aria-label={t('triggers.edit', { name: trigger.name })}
                      onClick={() => {
                        setEditor({ trigger });
                      }}
                    >
                      <Pencil aria-hidden />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      aria-label={t('triggers.delete', { name: trigger.name })}
                      onClick={() => {
                        setDeleting(trigger);
                      }}
                    >
                      <Trash2 aria-hidden />
                    </Button>
                  </div>
                ) : null}
              </li>
            ))}
          </ul>
        )}
        {canManage ? (
          <div className="flex items-center gap-3">
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={triggers.length >= MAX_TRIGGERS}
              onClick={() => {
                setEditor({ trigger: null });
              }}
            >
              <Plus aria-hidden />
              {t('triggers.add')}
            </Button>
            {triggers.length >= MAX_TRIGGERS ? (
              <span className="text-muted-foreground text-xs">
                {t('triggers.limit', { max: MAX_TRIGGERS })}
              </span>
            ) : null}
          </div>
        ) : null}
      </FormSection>
      {editor ? (
        <TriggerDialog
          key={editor.trigger?.id ?? 'new'}
          storeId={storeId}
          storeName={store.name}
          currency={store.defaultCurrency}
          trigger={editor.trigger}
          nextPosition={nextPosition}
          agents={agents.data ?? null}
          suggestedRepliesEnabled={settings.data?.behaviour.suggestedRepliesEnabled ?? true}
          onOpenChange={(open) => {
            if (!open) setEditor(null);
          }}
        />
      ) : null}
      <ConfirmDialog
        open={deleting !== null}
        onOpenChange={(open) => {
          if (!open) setDeleting(null);
        }}
        title={t('triggers.deleteTitle', { name: deleting?.name ?? '' })}
        description={t('triggers.deleteDescription')}
        confirmLabel={t('triggers.deleteConfirm')}
        pending={remove.isPending}
        onConfirm={() => {
          if (deleting) remove.mutate(deleting.id, { onSettled: () => setDeleting(null) });
        }}
      />
    </div>
  );
}
